'use client';

import React, { useState } from 'react';
import { SKIN_IMAGES, AdvancedHitbox, DEFAULT_ADVANCED_HITBOX } from './TargetSilhouette';

export type { AdvancedHitbox };
export { DEFAULT_ADVANCED_HITBOX };

export interface TrainerSettings {
  sensitivity: number;
  windowMs: number;
  crosshairSize: number;
  crosshairColor: string;
  showFlash: boolean;
  showGun: boolean;
  targetScale: number;
  aimDetectionSource: 'crosshair' | 'mouse';
  showDebugHitboxes: boolean;
  characterSpeed: number;
  travelDistance: number;
  targetMotion: 'pass-through' | 'stop-at-crosshair';
  spawnDirection: 'left' | 'right' | undefined;
  targetSkin: string;
  targetSkinWeapon?: 'vandal' | 'phantom';
  missedShotBehavior: 'disappear' | 'leave';
  intervalsEnabled: boolean;
}

export interface SpawnOffset {
  x: number;
  y: number;
}

interface SettingsPanelProps {
  settings: TrainerSettings;
  onChange: (settings: TrainerSettings) => void;
  spawnOffset?: SpawnOffset;
  onSpawnOffsetChange?: (offset: SpawnOffset) => void;
  onSpawnOffsetReset?: () => void;
  advancedHitbox?: AdvancedHitbox;
  onAdvancedHitboxChange?: (hitbox: AdvancedHitbox) => void;
  onAdvancedHitboxReset?: () => void;
}

// ── Presets ──────────────────────────────────────────────────────────────────
interface Preset {
  id: string;
  label: string;
  color: string;
  values: Partial<TrainerSettings>;
}

const PRESETS: Preset[] = [
  {
    id: 'beginner',
    label: 'Beginner',
    color: '#00FF94',
    values: {
      windowMs: 800,
      characterSpeed: 600,
      targetMotion: 'stop-at-crosshair',
      spawnDirection: 'left',
    },
  },
  {
    id: 'standard',
    label: 'Standard',
    color: '#00D4FF',
    values: {
      windowMs: 400,
      characterSpeed: 200,
      targetMotion: 'pass-through',
      spawnDirection: 'left',
    },
  },
  {
    id: 'expert',
    label: 'Expert',
    color: '#FFB347',
    values: {
      windowMs: 150,
      characterSpeed: 100,
      targetMotion: 'pass-through',
      spawnDirection: 'left',
    },
  },
  {
    id: 'insane',
    label: 'Insane',
    color: '#FF4655',
    values: {
      windowMs: 50,
      characterSpeed: 50,
      targetMotion: 'pass-through',
      spawnDirection: 'left',
    },
  },
];

// Target skin definitions
interface SkinOption {
  id: string;
  label: string;
  previewImages: string[];
  color: string;
  weapons?: { id: 'vandal' | 'phantom'; label: string; previewImage: string }[];
}

const SKIN_OPTIONS: SkinOption[] = [
  {
    id: 'red-outline',
    label: 'Red Outline',
    previewImages: SKIN_IMAGES['red-outline'],
    color: '#FF4655',
    weapons: [
      { id: 'vandal', label: 'Vandal', previewImage: SKIN_IMAGES['red-outline-vandal'][0] },
      { id: 'phantom', label: 'Phantom', previewImage: SKIN_IMAGES['red-outline-phantom'][0] },
    ],
  },
  {
    id: 'yellow-outline',
    label: 'Yellow Outline',
    previewImages: SKIN_IMAGES['yellow-outline'],
    color: '#FFD040',
    weapons: [
      { id: 'vandal', label: 'Vandal', previewImage: SKIN_IMAGES['yellow-outline-vandal'][0] },
      { id: 'phantom', label: 'Phantom', previewImage: SKIN_IMAGES['yellow-outline-phantom'][0] },
    ],
  },
  {
    id: 'purple-outline',
    label: 'Purple Outline',
    previewImages: SKIN_IMAGES['purple-outline'],
    color: '#C060FF',
    weapons: [
      { id: 'vandal', label: 'Vandal', previewImage: SKIN_IMAGES['purple-outline-vandal'][0] },
      { id: 'phantom', label: 'Phantom', previewImage: SKIN_IMAGES['purple-outline-phantom'][0] },
    ],
  },
];

const glassPanel: React.CSSProperties = {
  background: 'rgba(15, 15, 20, 0.72)',
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
  border: '1px solid rgba(255,255,255,0.10)',
  boxShadow: '0 8px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
};

// ── Core Settings Panel ───────────────────────────────────────────────────────
// isOpen / onOpenToggle: controls whether the panel is open or closed (icon-only)
// onClose: fully closes the panel back to icon-only state
export function CoreSettingsPanel({
  settings,
  onChange,
  isOpen,
  onOpenToggle,
  isMinimized,
  onMinimizeToggle,
  onClose,
  simulationEnabled = false,
}: Pick<SettingsPanelProps, 'settings' | 'onChange'> & {
  isOpen?: boolean;
  onOpenToggle?: () => void;
  isMinimized?: boolean;
  onMinimizeToggle?: () => void;
  onClose?: () => void;
  simulationEnabled?: boolean;
}) {
  const [respawnSpeedManuallySet, setRespawnSpeedManuallySet] = useState(false);
  // Two tabs: 'core' (timing/motion/speed) and 'appearance' (skin/hitbox/spawn)
  const [activeTab, setActiveTab] = useState<'core' | 'appearance'>('core');

  // Panel is open when isOpen is true — no minimized state
  const panelOpen = isOpen ?? false;

  const handleIconClick = () => {
    if (!panelOpen) {
      // Closed → open
      onOpenToggle?.();
    } else {
      // Open → close
      onClose?.();
    }
  };

  const update = (key: keyof TrainerSettings, value: TrainerSettings[keyof TrainerSettings]) => {
    onChange({ ...settings, [key]: value });
  };

  const applyPreset = (preset: Preset) => {
    const presetValues = respawnSpeedManuallySet
      ? Object.fromEntries(Object.entries(preset.values).filter(([k]) => k !== 'windowMs'))
      : preset.values;
    onChange({ ...settings, ...presetValues });
    setRespawnSpeedManuallySet(false);
  };

  // Icon-only state: panel is closed
  if (!panelOpen) {
    return (
      <button
        onClick={handleIconClick}
        className="flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-150"
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.1)',
          cursor: 'pointer',
        }}
        title="Core Settings"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    );
  }

  // Open state — always fully expanded
  return (
    <div
      className="select-none"
      style={{
        width: '220px',
      }}
    >
      <div className="rounded-2xl overflow-hidden" style={glassPanel}>
        {/* Header */}
        <div className="w-full flex items-center gap-3 px-3 py-3" style={{ cursor: 'default' }}>
          <button
            onClick={handleIconClick}
            className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
            style={{ background: 'rgba(0,212,255,0.15)', border: 'none', cursor: 'pointer', padding: 0 }}
            title="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="2.5" stroke="#00D4FF" strokeWidth="1.5"/>
              <path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.93 2.93l1.41 1.41M9.66 9.66l1.41 1.41M2.93 11.07l1.41-1.41M9.66 4.34l1.41-1.41" stroke="#00D4FF" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </button>
          <span
            className="text-xs font-semibold tracking-widest uppercase flex-1 text-left"
            style={{ color: 'rgba(232,232,240,0.9)', fontFamily: 'var(--font-sans)', letterSpacing: '0.12em' }}
          >
            Core Settings
          </span>
          {onClose && (
            <button
              onClick={onClose}
              className="flex items-center justify-center w-5 h-5 rounded-md hover:bg-white/10 transition-colors"
              style={{ border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(255,255,255,0.3)', background: 'transparent' }}
              title="Close"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {/* Tab bar */}
          <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {(['core', 'appearance'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="flex-1 py-1.5 transition-all duration-150"
                style={{
                  background: activeTab === tab ? 'rgba(0,212,255,0.10)' : 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab ? '2px solid #00D4FF' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: '8px',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: activeTab === tab ? 700 : 500,
                  color: activeTab === tab ? '#00D4FF' : 'rgba(255,255,255,0.35)',
                  letterSpacing: '0.10em',
                  textTransform: 'uppercase',
                }}
              >
                {tab === 'core' ? 'Timing & Motion' : 'Appearance'}
              </button>
            ))}
          </div>

          {/* Tab: Timing & Motion */}
          {activeTab === 'core' && (
            <div className="px-3 pb-3 space-y-4">
              {/* Presets */}
              <div className="pt-3">
                <span className="text-xs uppercase tracking-widest block mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
                  Presets
                </span>
                <div className="grid grid-cols-2 gap-1">
                  {PRESETS.map((preset) => {
                    const isActive = Object.entries(preset.values).every(
                      ([k, v]) => settings[k as keyof TrainerSettings] === v
                    );
                    return (
                      <button
                        key={preset.id}
                        onClick={() => applyPreset(preset)}
                        className="px-2 py-1.5 rounded-lg transition-all duration-150 text-center"
                        style={{
                          background: isActive ? `${preset.color}22` : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${isActive ? `${preset.color}66` : 'rgba(255,255,255,0.08)'}`,
                          cursor: 'pointer',
                          fontSize: '10px',
                          fontFamily: 'var(--font-sans)',
                          color: isActive ? preset.color : 'rgba(255,255,255,0.5)',
                          fontWeight: isActive ? 700 : 500,
                          letterSpacing: '0.05em',
                        }}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Respawn Speed */}
              <SettingRow label={settings.intervalsEnabled ? 'Respawn Speed (Max)' : 'Respawn Speed'} value={`${settings.windowMs}ms`}>
                <input
                  type="range" min={1} max={2000} step={1} value={settings.windowMs}
                  onChange={(e) => { update('windowMs', Number(e.target.value)); setRespawnSpeedManuallySet(true); }}
                  className="apple-slider w-full"
                />
              </SettingRow>

              {/* Character Speed */}
              <SettingRow label="Character Speed" value={`${settings.characterSpeed}ms`}>
                <input
                  type="range" min={50} max={1000} step={1} value={settings.characterSpeed}
                  onChange={(e) => update('characterSpeed', Number(e.target.value))}
                  className="apple-slider w-full"
                />
              </SettingRow>

              {/* Travel Distance */}
              <SettingRow label="Travel Distance" value={`${settings.travelDistance}%`}>
                <input
                  type="range" min={60} max={300} step={5} value={settings.travelDistance}
                  onChange={(e) => update('travelDistance', Number(e.target.value))}
                  className="apple-slider w-full"
                />
              </SettingRow>

              {/* Target Motion */}
              <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-xs uppercase tracking-widest block mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
                  Target Motion
                </span>
                <div className="flex flex-col gap-1">
                  {(['pass-through', 'stop-at-crosshair'] as const).map((mode) => {
                    const isActive = settings.targetMotion === mode;
                    return (
                      <button
                        key={mode}
                        onClick={() => update('targetMotion', mode)}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-150 text-left"
                        style={{
                          background: isActive ? 'rgba(0,212,255,0.14)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${isActive ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                          cursor: 'pointer', width: '100%',
                        }}
                      >
                        <div style={{ width: 8, height: 8, borderRadius: '50%', border: `1.5px solid ${isActive ? '#00D4FF' : 'rgba(255,255,255,0.3)'}`, background: isActive ? '#00D4FF' : 'transparent', flexShrink: 0 }} />
                        <span className="text-xs" style={{ color: isActive ? '#00D4FF' : 'rgba(255,255,255,0.5)', fontSize: '10px', fontFamily: 'var(--font-sans)' }}>
                          {mode === 'pass-through' ? 'Pass Through' : 'Stop at Crosshair'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Hitbox Size */}
              <SettingRow label="Hitbox Size" value={`${settings.targetScale.toFixed(1)}x`}>
                <input
                  type="range" min={0.2} max={6.0} step={0.1} value={settings.targetScale}
                  onChange={(e) => update('targetScale', Number(e.target.value))}
                  className="apple-slider w-full"
                />
              </SettingRow>

              {/* Missed Shot */}
              <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-xs uppercase tracking-widest block mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
                  Missed Shot
                </span>
                <div className="flex flex-col gap-1">
                  {([
                    { value: 'disappear', label: 'Disappear Target' },
                    { value: 'leave', label: 'Leave Target' },
                  ] as const).map(({ value, label }) => {
                    const isActive = settings.missedShotBehavior === value;
                    return (
                      <button
                        key={value}
                        onClick={() => update('missedShotBehavior', value)}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-150 text-left"
                        style={{
                          background: isActive ? 'rgba(0,212,255,0.14)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${isActive ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                          cursor: 'pointer', width: '100%',
                        }}
                      >
                        <div style={{ width: 8, height: 8, borderRadius: '50%', border: `1.5px solid ${isActive ? '#00D4FF' : 'rgba(255,255,255,0.3)'}`, background: isActive ? '#00D4FF' : 'transparent', flexShrink: 0 }} />
                        <span className="text-xs" style={{ color: isActive ? '#00D4FF' : 'rgba(255,255,255,0.5)', fontSize: '10px', fontFamily: 'var(--font-sans)' }}>
                          {label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Intervals */}
              <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
                    Intervals
                  </span>
                  <button
                    onClick={() => update('intervalsEnabled', !settings.intervalsEnabled)}
                    className="relative flex-shrink-0 transition-all duration-200"
                    style={{ width: '32px', height: '18px', borderRadius: '9px', background: settings.intervalsEnabled ? '#00D4FF' : 'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer', boxShadow: settings.intervalsEnabled ? '0 0 8px rgba(0,212,255,0.4)' : 'none' }}
                  >
                    <div
                      className="absolute top-0.5 transition-all duration-200"
                      style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#FFFFFF', left: settings.intervalsEnabled ? '15px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
                    />
                  </button>
                </div>
                <p style={{ fontSize: '8px', color: 'rgba(255,255,255,0.28)', lineHeight: 1.5, fontFamily: 'var(--font-sans)', marginTop: '4px' }}>
                  {settings.intervalsEnabled
                    ? 'Targets spawn at random intervals. Respawn Speed sets the maximum delay — each spawn lands randomly between 1ms and that value.'
                    : 'Targets spawn at fixed intervals. Enable for randomized timing so you can\'t anticipate the next spawn.'}
                </p>
              </div>
            </div>
          )}

          {/* Tab: Appearance */}
          {activeTab === 'appearance' && (
            <div className="px-3 pb-3 space-y-4">
              {/* Spawn Direction */}
              <div className="pt-3">
                <div className="flex items-center justify-between mb-1">
                  <span
                    className="text-xs uppercase tracking-widest"
                    style={{ color: simulationEnabled ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.35)', fontSize: '9px', transition: 'color 0.2s' }}
                  >
                    Spawn Direction
                  </span>
                  {simulationEnabled && (
                    <span
                      title="Controlled by Simulation Mode"
                      style={{
                        fontSize: '8px',
                        color: '#A78BFA',
                        fontFamily: 'var(--font-sans)',
                        letterSpacing: '0.04em',
                        opacity: 0.85,
                        cursor: 'help',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                      }}
                    >
                      <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                        <circle cx="5" cy="5" r="4" stroke="#A78BFA" strokeWidth="1.2"/>
                        <path d="M5 4.5v2.5" stroke="#A78BFA" strokeWidth="1.2" strokeLinecap="round"/>
                        <circle cx="5" cy="3.2" r="0.6" fill="#A78BFA"/>
                      </svg>
                      Sim Mode
                    </span>
                  )}
                </div>
                <span className="text-xs block mb-2" style={{ color: simulationEnabled ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.25)', fontSize: '8px', transition: 'color 0.2s' }}>
                  Override per-angle wall side
                </span>
                <div
                  className="flex gap-1"
                  style={{
                    opacity: simulationEnabled ? 0.35 : 1,
                    pointerEvents: simulationEnabled ? 'none' : 'auto',
                    transition: 'opacity 0.2s',
                  }}
                  title={simulationEnabled ? 'Controlled by Simulation Mode' : undefined}
                >
                  <button
                    onClick={() => update('spawnDirection', undefined)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg transition-all duration-150"
                    style={{
                      background: settings.spawnDirection === undefined ? 'rgba(0,255,148,0.14)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${settings.spawnDirection === undefined ? 'rgba(0,255,148,0.4)' : 'rgba(255,255,255,0.08)'}`,
                      cursor: 'pointer',
                    }}
                  >
                    <span className="text-xs" style={{ color: settings.spawnDirection === undefined ? '#00FF94' : 'rgba(255,255,255,0.5)', fontSize: '10px', fontFamily: 'var(--font-sans)' }}>
                      Auto
                    </span>
                  </button>
                  {(['left', 'right'] as const).map((dir) => {
                    const isActive = settings.spawnDirection === dir;
                    return (
                      <button
                        key={dir}
                        onClick={() => update('spawnDirection', dir)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg transition-all duration-150"
                        style={{
                          background: isActive ? 'rgba(0,212,255,0.14)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${isActive ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                          cursor: 'pointer',
                        }}
                      >
                        {dir === 'left' ? (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M8 5H2M2 5l3-3M2 5l3 3" stroke={isActive ? '#00D4FF' : 'rgba(255,255,255,0.4)'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5h6M8 5L5 2M8 5L5 8" stroke={isActive ? '#00D4FF' : 'rgba(255,255,255,0.4)'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                        <span className="text-xs capitalize" style={{ color: isActive ? '#00D4FF' : 'rgba(255,255,255,0.5)', fontSize: '10px', fontFamily: 'var(--font-sans)' }}>
                          {dir}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Target Skin */}
              <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-xs uppercase tracking-widest block mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
                  Target Skin
                </span>
                <div className="grid grid-cols-2 gap-1.5">
                  {SKIN_OPTIONS.map((skin) => {
                    const isActive = settings.targetSkin === skin.id;
                    const previewSrc = skin.previewImages[0];
                    return (
                      <button
                        key={skin.id}
                        onClick={() => update('targetSkin', skin.id)}
                        className="flex flex-col items-center gap-1 p-1.5 rounded-xl transition-all duration-150"
                        style={{
                          background: isActive ? `${skin.color}18` : 'rgba(255,255,255,0.04)',
                          border: `1.5px solid ${isActive ? `${skin.color}60` : 'rgba(255,255,255,0.08)'}`,
                          cursor: 'pointer',
                          boxShadow: isActive ? `0 0 8px ${skin.color}30` : 'none',
                        }}
                      >
                        <div
                          className="rounded-lg overflow-hidden flex items-center justify-center"
                          style={{ width: '44px', height: '52px', background: 'rgba(0,0,0,0.35)', border: `1px solid ${isActive ? `${skin.color}40` : 'rgba(255,255,255,0.06)'}` }}
                        >
                          {previewSrc ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={previewSrc} alt={skin.label} style={{ width: '36px', height: '48px', objectFit: 'contain', objectPosition: 'top center', imageRendering: 'auto' }} draggable={false} />
                          ) : (
                            <div className="flex flex-col items-center gap-0.5">
                              <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#C8B89A', border: '1.5px solid #00D4FF' }} />
                              <div style={{ width: 4, height: 3, background: '#A09080' }} />
                              <div style={{ width: 18, height: 22, background: '#A09080', borderRadius: 2 }} />
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: '8px', fontFamily: 'var(--font-sans)', color: isActive ? skin.color : 'rgba(255,255,255,0.4)', fontWeight: isActive ? 700 : 500, letterSpacing: '0.04em', textAlign: 'center', lineHeight: 1.2 }}>
                          {skin.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Weapon sub-options */}
                {(() => {
                  const activeSkin = SKIN_OPTIONS.find((s) => s.id === settings.targetSkin);
                  if (!activeSkin?.weapons) return null;
                  return (
                    <div className="mt-2">
                      <div className="flex gap-1.5">
                        {activeSkin.weapons.map((w) => {
                          const isWeaponActive = settings.targetSkinWeapon === w.id;
                          return (
                            <button
                              key={w.id}
                              onClick={() => update('targetSkinWeapon', isWeaponActive ? undefined : w.id)}
                              className="flex-1 flex flex-col items-center gap-1 p-1.5 rounded-xl transition-all duration-150"
                              style={{
                                background: isWeaponActive ? `${activeSkin.color}22` : 'rgba(255,255,255,0.04)',
                                border: `1.5px solid ${isWeaponActive ? `${activeSkin.color}70` : 'rgba(255,255,255,0.08)'}`,
                                cursor: 'pointer',
                                boxShadow: isWeaponActive ? `0 0 6px ${activeSkin.color}28` : 'none',
                              }}
                            >
                              <div className="rounded-lg overflow-hidden flex items-center justify-center" style={{ width: '44px', height: '52px', background: 'rgba(0,0,0,0.35)', border: `1px solid ${isWeaponActive ? `${activeSkin.color}40` : 'rgba(255,255,255,0.06)'}` }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img src={w.previewImage} alt={w.label} style={{ width: '36px', height: '48px', objectFit: 'contain', objectPosition: 'top center', imageRendering: 'auto' }} draggable={false} />
                              </div>
                              <span style={{ fontSize: '8px', fontFamily: 'var(--font-sans)', color: isWeaponActive ? activeSkin.color : 'rgba(255,255,255,0.4)', fontWeight: isWeaponActive ? 700 : 500, letterSpacing: '0.04em', textAlign: 'center', lineHeight: 1.2 }}>
                                {w.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Advanced Settings Panel ───────────────────────────────────────────────────
export function AdvancedSettingsPanel({
  settings,
  onChange,
  spawnOffset = { x: 0, y: 0 },
  onSpawnOffsetChange,
  onSpawnOffsetReset,
  advancedHitbox = DEFAULT_ADVANCED_HITBOX,
  onAdvancedHitboxChange,
  onAdvancedHitboxReset,
  isOpen,
  onOpenToggle,
  isMinimized,
  onMinimizeToggle,
  onClose,
}: SettingsPanelProps & {
  isOpen?: boolean;
  onOpenToggle?: () => void;
  isMinimized?: boolean;
  onMinimizeToggle?: () => void;
  onClose?: () => void;
}) {
  const panelOpen = isOpen ?? false;

  const handleIconClick = () => {
    if (!panelOpen) {
      onOpenToggle?.();
    } else {
      onClose?.();
    }
  };

  const update = (key: keyof TrainerSettings, value: TrainerSettings[keyof TrainerSettings]) => {
    onChange({ ...settings, [key]: value });
  };

  const nudge = (dx: number, dy: number) => {
    if (!onSpawnOffsetChange) return;
    onSpawnOffsetChange({ x: spawnOffset.x + dx, y: spawnOffset.y + dy });
  };

  const formatOffset = (v: number) => (v >= 0 ? `+${v}px` : `${v}px`);

  // Icon-only state
  if (!panelOpen) {
    return (
      <button
        onClick={handleIconClick}
        className="flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-150"
        style={{
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.1)',
          cursor: 'pointer',
        }}
        title="Advanced Settings"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
          <path d="M4 6h16M4 12h16M4 18h16" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round"/>
          <circle cx="9" cy="6" r="2" fill="rgba(255,255,255,0.7)"/>
          <circle cx="15" cy="12" r="2" fill="rgba(255,255,255,0.7)"/>
          <circle cx="9" cy="18" r="2" fill="rgba(255,255,255,0.7)"/>
        </svg>
      </button>
    );
  }

  return (
    <div
      className="select-none"
      style={{
        width: '220px',
      }}
    >
      <div className="rounded-2xl overflow-hidden" style={glassPanel}>
        {/* Header */}
        <div className="w-full flex items-center gap-3 px-3 py-3" style={{ cursor: 'default' }}>
          <button
            onClick={handleIconClick}
            className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
            style={{ background: 'rgba(255,70,85,0.15)', border: 'none', cursor: 'pointer', padding: 0 }}
            title="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 4h10M2 7h10M2 10h10" stroke="#FF4655" strokeWidth="1.3" strokeLinecap="round"/>
              <circle cx="5" cy="4" r="1.5" fill="#FF4655"/>
              <circle cx="9" cy="7" r="1.5" fill="#FF4655"/>
              <circle cx="5" cy="10" r="1.5" fill="#FF4655"/>
            </svg>
          </button>
          <span
            className="text-xs font-semibold tracking-widest uppercase flex-1 text-left"
            style={{ color: 'rgba(232,232,240,0.9)', fontFamily: 'var(--font-sans)', letterSpacing: '0.12em' }}
          >
            Advanced
          </span>
          {onClose && (
            <button
              onClick={onClose}
              className="flex items-center justify-center w-5 h-5 rounded-md hover:bg-white/10 transition-colors"
              style={{ border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(255,255,255,0.3)', background: 'transparent' }}
              title="Close"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-3 pb-3 space-y-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>

          {/* Spawn Offset D-Pad */}
          <div className="pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
                Spawn Offset
              </span>
              <span className="text-xs font-semibold font-mono" style={{ color: 'rgba(0,212,255,0.9)', fontSize: '10px' }}>
                {formatOffset(spawnOffset.x)}, {formatOffset(spawnOffset.y)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex flex-col items-center gap-0.5">
                <DPadButton onClick={() => nudge(0, 1)} title="Nudge up">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 2l3.5 5.5H1.5L5 2z" fill="currentColor"/></svg>
                </DPadButton>
                <div className="flex items-center gap-0.5">
                  <DPadButton onClick={() => nudge(-1, 0)} title="Nudge left">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l5.5-3.5v7L2 5z" fill="currentColor"/></svg>
                  </DPadButton>
                  <div style={{ width: 20, height: 20, borderRadius: 4, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />
                  </div>
                  <DPadButton onClick={() => nudge(1, 0)} title="Nudge right">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M8 5L2.5 1.5v7L8 5z" fill="currentColor"/></svg>
                  </DPadButton>
                </div>
                <DPadButton onClick={() => nudge(0, -1)} title="Nudge down">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 8L1.5 2.5h7L5 8z" fill="currentColor"/></svg>
                </DPadButton>
              </div>
              <button
                onClick={onSpawnOffsetReset}
                title="Reset offset to 0,0"
                className="flex items-center gap-1 px-2 py-1 rounded-lg transition-all duration-150"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: '9px', letterSpacing: '0.06em', fontFamily: 'var(--font-sans)', height: 28 }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,212,255,0.12)'; (e.currentTarget as HTMLButtonElement).style.color = '#00D4FF'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,212,255,0.3)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
              >
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1 5a4 4 0 1 0 1.2-2.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M1 2v3h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Reset
              </button>
            </div>
          </div>

          {/* Advanced Hitbox Fine-Tune */}
          <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
                Hitbox Fine-Tune
              </span>
              <button
                onClick={onAdvancedHitboxReset}
                title="Reset hitbox fine-tune to 1.0x"
                className="flex items-center gap-1 px-2 py-0.5 rounded-lg transition-all duration-150"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: '9px', letterSpacing: '0.06em', fontFamily: 'var(--font-sans)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.12)'; (e.currentTarget as HTMLButtonElement).style.color = '#FF4655'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,70,85,0.3)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
              >
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1 5a4 4 0 1 0 1.2-2.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M1 2v3h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Reset
              </button>
            </div>
            <span className="text-xs block mb-2" style={{ color: 'rgba(255,255,255,0.2)', fontSize: '8px' }}>
              Multiplies on top of Core Hitbox Size
            </span>
            <div className="space-y-0">
              <SettingRow label="Head Width" value={`${advancedHitbox.headW.toFixed(2)}x`}>
                <input type="range" min={0.1} max={3.0} step={0.05} value={advancedHitbox.headW} onChange={(e) => onAdvancedHitboxChange?.({ ...advancedHitbox, headW: Number(e.target.value) })} className="apple-slider w-full"/>
              </SettingRow>
              <SettingRow label="Head Height" value={`${advancedHitbox.headH.toFixed(2)}x`}>
                <input type="range" min={0.1} max={3.0} step={0.05} value={advancedHitbox.headH} onChange={(e) => onAdvancedHitboxChange?.({ ...advancedHitbox, headH: Number(e.target.value) })} className="apple-slider w-full"/>
              </SettingRow>
              <SettingRow label="Body Width" value={`${advancedHitbox.bodyW.toFixed(2)}x`}>
                <input type="range" min={0.1} max={3.0} step={0.05} value={advancedHitbox.bodyW} onChange={(e) => onAdvancedHitboxChange?.({ ...advancedHitbox, bodyW: Number(e.target.value) })} className="apple-slider w-full"/>
              </SettingRow>
              <SettingRow label="Body Height" value={`${advancedHitbox.bodyH.toFixed(2)}x`}>
                <input type="range" min={0.1} max={3.0} step={0.05} value={advancedHitbox.bodyH} onChange={(e) => onAdvancedHitboxChange?.({ ...advancedHitbox, bodyH: Number(e.target.value) })} className="apple-slider w-full"/>
              </SettingRow>
            </div>
          </div>

          {/* Crosshair Size */}
          <SettingRow label="Crosshair" value={`${settings.crosshairSize}px`}>
            <input type="range" min={2} max={8} step={1} value={settings.crosshairSize} onChange={(e) => update('crosshairSize', Number(e.target.value))} className="apple-slider w-full"/>
          </SettingRow>

          {/* Toggles */}
          <div className="space-y-2 pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <ToggleRow label="Hit Flash" value={settings.showFlash} onChange={(v) => update('showFlash', v)} />
            <ToggleRow label="Show Debug Hitboxes" value={settings.showDebugHitboxes} onChange={(v) => update('showDebugHitboxes', v)} />
          </div>

          {/* Aim Detection Source */}
          <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span className="text-xs uppercase tracking-widest block mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
              Aim Detection Source
            </span>
            <div className="flex flex-col gap-1">
              {(['crosshair', 'mouse'] as const).map((mode) => {
                const isActive = settings.aimDetectionSource === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => update('aimDetectionSource', mode)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-150 text-left"
                    style={{
                      background: isActive ? 'rgba(0,212,255,0.14)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${isActive ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                      cursor: 'pointer', width: '100%',
                    }}
                  >
                    <div style={{ width: 8, height: 8, borderRadius: '50%', border: `1.5px solid ${isActive ? '#00D4FF' : 'rgba(255,255,255,0.3)'}`, background: isActive ? '#00D4FF' : 'transparent', flexShrink: 0 }} />
                    <span className="text-xs" style={{ color: isActive ? '#00D4FF' : 'rgba(255,255,255,0.5)', fontSize: '10px', fontFamily: 'var(--font-sans)' }}>
                      {mode === 'crosshair' ? 'Crosshair' : 'Mouse Cursor'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Default export = CoreSettingsPanel (backward compat)
export default CoreSettingsPanel;

// ── Shared sub-components ─────────────────────────────────────────────────────
function DPadButton({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="flex items-center justify-center rounded transition-all duration-100"
      style={{ width: 20, height: 20, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0 }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,212,255,0.18)'; (e.currentTarget as HTMLButtonElement).style.color = '#00D4FF'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(0,212,255,0.35)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.6)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.12)'; }}
    >
      {children}
    </button>
  );
}

function SettingRow({ label, value, children }: { label: string; value: string; children: React.ReactNode }) {
  return (
    <div className="pt-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>
          {label}
        </span>
        <span className="text-xs font-semibold font-mono" style={{ color: 'rgba(0,212,255,0.9)', fontSize: '10px' }}>
          {value}
        </span>
      </div>
      {children}
    </div>
  );
}

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
        {label}
      </span>
      <button
        onClick={() => onChange(!value)}
        className="relative flex-shrink-0 transition-all duration-200"
        style={{ width: '32px', height: '18px', borderRadius: '9px', background: value ? '#00D4FF' : 'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer', boxShadow: value ? '0 0 8px rgba(0,212,255,0.4)' : 'none' }}
      >
        <div
          className="absolute top-0.5 transition-all duration-200"
          style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#FFFFFF', left: value ? '15px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
        />
      </button>
    </div>
  );
}
